type FormatNumberOptions = {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    fallback?: string;
};

const formatIntegerPartWithApostrophes = (value: number) => {
    const integerPart = Math.trunc(Math.abs(value)).toString();
    return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

export const formatNumberWithApostrophes = (value: number, options: FormatNumberOptions = {}) => {
    const { minimumFractionDigits = 0, maximumFractionDigits = minimumFractionDigits, fallback = '0' } = options;
    if (!Number.isFinite(value)) return fallback;

    const safeMaximumFractionDigits = Math.max(minimumFractionDigits, maximumFractionDigits);
    const safeValue = Number(value);
    const absValue = Math.abs(safeValue);
    const sign = safeValue < 0 ? '-' : '';
    const fixedValue = absValue.toFixed(safeMaximumFractionDigits);
    const [integerPart, rawDecimalPart = ''] = fixedValue.split('.');
    const groupedIntegerPart = absValue > 999 ? formatIntegerPartWithApostrophes(absValue) : integerPart;

    if (safeMaximumFractionDigits === 0) {
        return `${sign}${groupedIntegerPart}`;
    }

    let decimalPart = rawDecimalPart;
    while (decimalPart.length > minimumFractionDigits && decimalPart.endsWith('0')) {
        decimalPart = decimalPart.slice(0, -1);
    }

    return decimalPart ? `${sign}${groupedIntegerPart}.${decimalPart}` : `${sign}${groupedIntegerPart}`;
};

export const formatMetricNumber = (value: number) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    const absValue = Math.abs(safeValue);
    if (absValue === 0) return '0';

    const integerDigits = Math.trunc(absValue).toString().length;
    const fractionDigits = integerDigits >= 3 ? 0 : integerDigits === 2 ? 1 : 2;

    return formatNumberWithApostrophes(safeValue, {
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    });
};

export const getMetricFractionDigits = (value: number) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    const absValue = Math.abs(safeValue);
    if (absValue === 0) return 0;

    const integerDigits = Math.trunc(absValue).toString().length;
    return integerDigits >= 3 ? 0 : integerDigits === 2 ? 1 : 2;
};

export const formatMetricPair = (minValue: number, maxValue: number) => {
    const nonZeroFractionDigits = [minValue, maxValue].filter((value) => value !== 0).map((value) => getMetricFractionDigits(value));
    const sharedFractionDigits = nonZeroFractionDigits.length ? Math.max(...nonZeroFractionDigits) : 0;

    const formatValue = (value: number) =>
        formatNumberWithApostrophes(value, {
            minimumFractionDigits: value === 0 ? 0 : Math.max(getMetricFractionDigits(value), sharedFractionDigits),
            maximumFractionDigits: value === 0 ? 0 : Math.max(getMetricFractionDigits(value), sharedFractionDigits),
        });

    return {
        min: formatValue(minValue),
        max: formatValue(maxValue),
    };
};

export const formatSensorPointCount = (value: number) => {
    const safeValue = Number.isFinite(value) ? Math.trunc(value) : 0;
    return formatNumberWithApostrophes(safeValue, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};
